import React, { useState, useEffect } from 'react';
import { mcrService } from '../../../../services/mcrService';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  useTheme,
  Divider
} from '@mui/material';
import {
  Settings,
  Add,
  ExpandMore,
  ExpandLess,
  Folder,
  Edit,
  Delete,
  Article,
  ChevronRight
} from '@mui/icons-material';

const ServicesManagement = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  
  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [basicAmount, setBasicAmount] = useState('');
  const [dstAmount, setDstAmount] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  
  // Edit states
  const [editMode, setEditMode] = useState(null); // 'category' or 'service'
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // UI states
  const [expandedCategories, setExpandedCategories] = useState([]);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const result = await mcrService.getAllServicesHierarchy();
    if (result.success) {
      setCategories(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const toggleCategory = (categoryId) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  // Category handlers
  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;

    setLoading(true);
    let result;

    if (editMode === 'category' && editingItem) {
      result = await mcrService.updateCategory(editingItem.id, categoryName);
      if (result.success) {
        setSuccess('Category updated successfully');
      }
    } else {
      result = await mcrService.addCategory(categoryName);
      if (result.success) {
        setSuccess('Category added successfully');
      }
    }

    if (result.success) {
      setCategoryName('');
      setShowCategoryModal(false);
      setEditMode(null);
      setEditingItem(null);
      loadAllData();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? All services under it will also be deleted.')) return;

    setLoading(true);
    const result = await mcrService.deleteCategory(categoryId);
    if (result.success) {
      setSuccess('Category deleted successfully');
      loadAllData();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Service handlers
  const handleSaveService = async () => {
    if (!serviceName.trim() || !basicAmount) return;

    setLoading(true);
    const serviceData = {
      category_id: selectedCategory.id,
      name: serviceName,
      basic_amount: parseFloat(basicAmount),
      dst_amount: dstAmount ? parseFloat(dstAmount) : 0,
      description: serviceDescription
    };

    let result;
    if (editMode === 'service' && editingItem) {
      result = await mcrService.updateService(editingItem.id, serviceData);
      if (result.success) {
        setSuccess('Service updated successfully');
      }
    } else {
      result = await mcrService.addService(serviceData);
      if (result.success) {
        setSuccess('Service added successfully');
      }
    }

    if (result.success) {
      setServiceName('');
      setBasicAmount('');
      setDstAmount('');
      setServiceDescription('');
      setShowServiceModal(false);
      setEditMode(null);
      setEditingItem(null);
      loadAllData();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;

    setLoading(true);
    const result = await mcrService.deleteService(serviceId);
    if (result.success) {
      setSuccess('Service deleted successfully');
      loadAllData();
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Settings sx={{ mr: 1 }} />
              <Typography variant="h5" fontWeight="bold">
                Services Management
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage service categories and pricing
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => {
              setCategoryName('');
              setEditMode(null);
              setEditingItem(null);
              setShowCategoryModal(true);
            }}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          >
            Add Category
          </Button>
        </Paper>

        {/* Categories List */}
        {loading && categories.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : categories.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'grey.50',
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2
            }}
          >
            <Folder sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No categories yet. Add one to get started!
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {categories.map((category) => (
              <Paper key={category.id} elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'grey.50' },
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleCategory(category.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(category.id);
                      }}
                    >
                      {expandedCategories.includes(category.id) ? <ExpandLess /> : <ChevronRight />}
                    </IconButton>
                    <Folder color="primary" />
                    <Box>
                      <Typography variant="h6" fontWeight="medium">
                        {category.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {category.services?.length || 0} services
                      </Typography>
                    </Box>
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setCategoryName(category.name);
                        setEditMode('category');
                        setEditingItem(category);
                        setShowCategoryModal(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedCategories.includes(category.id)} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    {(!category.services || category.services.length === 0) ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                        No services yet.
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {category.services.map((service) => (
                          <Grid item xs={12} md={6} lg={4} key={service.id}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  boxShadow: 1
                                }
                              }}
                            >
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Article color="success" fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {service.name}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setServiceName(service.name);
                                        setBasicAmount(service.basic_amount);
                                        setDstAmount(service.dst_amount || '');
                                        setServiceDescription(service.description || '');
                                        setSelectedCategory(category);
                                        setEditMode('service');
                                        setEditingItem(service);
                                        setShowServiceModal(true);
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteService(service.id)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                                
                                {service.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {service.description}
                                  </Typography>
                                )}
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                  <Chip
                                    label={`Basic: ₱${parseFloat(service.basic_amount).toFixed(2)}`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                  />
                                  {service.dst_amount > 0 && (
                                    <Chip
                                      label={`DST: ₱${parseFloat(service.dst_amount).toFixed(2)}`}
                                      size="small"
                                      variant="outlined"
                                      color="warning"
                                    />
                                  )}
                                  <Chip
                                    label={`Total: ₱${(parseFloat(service.basic_amount) + parseFloat(service.dst_amount)).toFixed(2)}`}
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => {
                          setSelectedCategory(category);
                          setServiceName('');
                          setBasicAmount('');
                          setDstAmount('');
                          setServiceDescription('');
                          setEditMode(null);
                          setEditingItem(null);
                          setShowServiceModal(true);
                        }}
                      >
                        Add Service
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            ))}
          </Box>
        )}

        {/* Category Modal */}
        <Dialog open={showCategoryModal} onClose={() => setShowCategoryModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editMode === 'category' ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              variant="outlined"
              placeholder="e.g., Birth Certificates"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCategoryModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} variant="contained" disabled={loading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Service Modal */}
        <Dialog open={showServiceModal} onClose={() => setShowServiceModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editMode === 'service' ? 'Edit Service' : 'Add Service'}
            {selectedCategory && ` - ${selectedCategory.name}`}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Name"
                  placeholder="e.g., PSA Birth Certificate"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Basic Amount *"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={basicAmount}
                  onChange={(e) => setBasicAmount(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="DST Amount (Optional)"
                  type="number"
                  inputProps={{ step: "0.01" }}
                  value={dstAmount}
                  onChange={(e) => setDstAmount(e.target.value)}
                  helperText="Documentary Stamp Tax"
                />
              </Grid>
              {basicAmount && (
                <Grid item xs={12}>
                  <Alert severity="info" variant="outlined">
                    Total Amount: <strong>₱{(parseFloat(basicAmount || 0) + parseFloat(dstAmount || 0)).toFixed(2)}</strong>
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description (Optional)"
                  placeholder="Service description"
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowServiceModal(false)}>Cancel</Button>
            <Button onClick={handleSaveService} variant="contained" disabled={loading}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Feedback Snackbar */}
        <Snackbar
          open={Boolean(error || success)}
          autoHideDuration={3000}
          onClose={() => { setError(''); setSuccess(''); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => { setError(''); setSuccess(''); }}
            severity={error ? 'error' : 'success'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error || success}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ServicesManagement;
